// StreakSettingsForm.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";

const StreakSettingsForm = ({ targetId, targetCode, targetName, targetType = "Child" }) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;
    const user  = authState?.user;

    const [triggerMode, setTriggerMode] = useState("Calendar");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const ownerType = (authState?.user?.role || "").toUpperCase() === "MENTOR" ? "Mentor" : "Parent";

    useEffect(() => {
        if (targetId) loadSettings();
    }, [targetId]);

    const loadSettings = async () => {
        try {
            const res = await fetchData({
                url: "/pd/streaks/settings-get",
                method: "post",
                data: { ownerId: user?._id, targetId },
                headers: { Authorization: token },
            });
            const existing = res?.data?.[0];
            if (existing) {
                setTriggerMode(existing.triggerMode || "Calendar");
                setStartDate(existing.startDate?.substring(0, 10) || "");
                setEndDate(existing.endDate?.substring(0, 10) || "");
            }
        } catch (err) {
            console.error("loadSettings error:", err);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setMessage(null);
            const res = await fetchData({
                url: "/pd/streaks/settings-save",
                method: "post",
                data: {
                    ownerId: user?._id,
                    ownerCode: user?.think5whyscode,
                    ownerName: user?.name,
                    ownerType,
                    targetId, targetCode, targetName, targetType,
                    startDate, endDate,
                    triggerMode,
                },
                headers: { Authorization: token },
            });
            if (res?.success) {
                setMessage({ type: "success", text: "Settings saved" });
            } else {
                setMessage({ type: "warning", text: res?.message || "Save failed" });
            }
        } catch (err) {
            setMessage({ type: "error", text: "Save failed" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-3 bg-gray-900 p-4 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="text-cyan-300 font-bold text-sm">
                ⚙️ Streak Approval Settings — {targetName || targetCode}
            </div>

            <div className="space-y-2">
                <label className="text-[11px] text-gray-300">Milestone trigger</label>
                <div className="flex gap-2">
                    {["Calendar", "Settings"].map((mode) => (
                        <button
                            key={mode}
                            onClick={() => setTriggerMode(mode)}
                            className={`w-fit px-3 py-1.5 rounded-xl text-xs font-bold border ${
                                triggerMode === mode
                                    ? "bg-cyan-500 border-cyan-500 text-black"
                                    : "bg-gray-800 border-gray-700 text-gray-200"
                            }`}
                        >
                            {mode === "Calendar" ? "📅 Calendar (7 / 30 / 90 days)" : "🎯 Settings (30 / 60 / 90 days)"}
                        </button>
                    ))}
                </div>
                <div className="text-[10px] text-gray-500">
                    Calendar mode triggers off raw activity streak length. Settings mode triggers off the 30/60/90-day
                    milestones you configure separately — note the day-counts differ between the two modes.
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-[10px] text-gray-400">Programme start</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl px-2 py-1.5 text-xs text-white"
                    />
                </div>
                <div>
                    <label className="text-[10px] text-gray-400">Programme end</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl px-2 py-1.5 text-xs text-white"
                    />
                </div>
            </div>

            <button
                onClick={handleSave}
                disabled={loading || !startDate || !endDate}
                className="w-fit px-3 py-1.5 rounded-xl bg-cyan-500 text-black text-xs font-black disabled:opacity-50"
            >
                {loading ? "Saving..." : "💾 Save Settings"}
            </button>

            {message && (
                <div className={`text-xs p-2 rounded-xl ${
                    message.type === "success" ? "bg-green-900/30 text-green-300" : "bg-yellow-900/30 text-yellow-300"
                }`}>
                    {message.text}
                </div>
            )}
        </div>
    );
};

export default StreakSettingsForm;